'use no memo';

import {Switch} from 'platform/components';
import {match} from 'ts-pattern';
import {DeepReadonly} from 'utility-types';

import {ReactElement, useCallback, useEffect, useState} from 'react';
import {useParams} from 'react-router-dom';

import {isNil} from 'ramda';

import {
  AdvertisementStatusEnum,
  useDisablePlatformForVehicleMutation,
  useEnablePlatformForVehicleMutation,
} from '@dms/api/sales';
import {AdvertisingPlatformCodeEnum, FullVehicle} from '@dms/api/shared';
import {handleApiError} from '@dms/shared';

import {useBoolean} from 'shared/web';

import {useAdvertisementPolling} from '../contexts/AdvertisementPollingContext';

type SwitcherProps = {
  code: AdvertisingPlatformCodeEnum;
  value?: boolean;
  currentStatus: AdvertisementStatusEnum;
  vehicleSaleState: FullVehicle['saleState'];
  inSale: boolean;
  isDisabled: boolean;
};

export const PlatformSwitcher = ({
  value: initialValue,
  code,
  currentStatus,
  vehicleSaleState,
  inSale,
  isDisabled,
}: DeepReadonly<SwitcherProps>): ReactElement => {
  const [value, setValue] = useState<boolean>(!!initialValue);
  const {id: vehicleId} = useParams();
  const [isProcessing, startProcess, stopProcess] = useBoolean(true);
  const {resetPollingInterval} = useAdvertisementPolling();
  const [enablePlatform] = useEnablePlatformForVehicleMutation();
  const [disablePlatform] = useDisablePlatformForVehicleMutation();

  useEffect(() => {
    if (!isNil(initialValue)) {
      //eslint-disable-next-line react-hooks/set-state-in-effect
      setValue(initialValue);
    }
  }, [initialValue]);

  useEffect(() => {
    match(currentStatus)
      .with(
        AdvertisementStatusEnum.PUBLISHING,
        AdvertisementStatusEnum.UPDATING,
        AdvertisementStatusEnum.UNPUBLISHING,
        startProcess
      )
      .otherwise(stopProcess);
  }, [currentStatus, startProcess, stopProcess, inSale, value, vehicleSaleState]);

  const onChange = useCallback(
    (newValue: boolean) => {
      if (isNil(vehicleId)) {
        return;
      }
      resetPollingInterval();
      setValue(newValue);
      startProcess();

      if (newValue) {
        enablePlatform({vehicleId, platformCode: code}).unwrap().catch(handleApiError);
      } else {
        disablePlatform({vehicleId, platformCode: code}).unwrap().catch(handleApiError);
      }
    },
    [resetPollingInterval, startProcess, enablePlatform, disablePlatform, vehicleId, code]
  );

  return (
    <Switch
      value={value}
      onChange={onChange}
      name={`advertisement-platform-switcher-${code}`}
      isDisabled={isProcessing || isDisabled}
      data-testid={`switch-advertisement-platforms-switcher-${code}`}
    />
  );
};
